import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { StandardMaterialsContent } from '@/pages/StandardMaterialsPage';
import { getMatchingMatches, getMatchingStats, getMatchingCockpit, getSites } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  ArrowRightLeft, TrendingUp, Package, Factory, Search,
  ExternalLink, ChevronDown, ChevronUp, Target, BarChart3, ClipboardList,
  Gauge, DollarSign, Layers, Activity
} from 'lucide-react';

const AGE_COLORS = {
  '0_90': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  '91_180': 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-400',
  '181_360': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  'gt_360': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const fmtNum = (v, dec = 1) => v != null ? Number(v).toLocaleString('de-DE', { minimumFractionDigits: dec, maximumFractionDigits: dec }) : '-';
const fmtEUR = (v) => v != null ? `${fmtNum(v, 2)} kEUR` : '-';

// Matching content (existing matching dashboard)
const MatchingContent = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const AGE_LABELS = {
    '0_90': t('matching.age0_90'),
    '91_180': t('matching.age91_180'),
    '181_360': t('matching.age181_360'),
    'gt_360': t('matching.ageGt360'),
  };

  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState('all');
  const [demandSiteFilter, setDemandSiteFilter] = useState('all');
  const [manufacturerSearch, setManufacturerSearch] = useState('');
  const [sortField, setSortField] = useState('quantity');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, matchesRes, sitesRes] = await Promise.all([
        getMatchingStats(),
        getMatchingMatches({}),
        getSites(user.group_id),
      ]);
      setStats(statsRes.data);
      setMatches(matchesRes.data.matches || []);
      setSites(sitesRes.data || []);
    } catch (err) {
      console.error('Failed to load matching data', err);
    } finally {
      setLoading(false);
    }
  };

  const reloadMatches = async () => {
    try {
      const params = {};
      if (siteFilter !== 'all') params.site_filter = siteFilter;
      if (demandSiteFilter !== 'all') params.demand_site_filter = demandSiteFilter;
      if (manufacturerSearch.trim()) params.manufacturer_filter = manufacturerSearch.trim();
      const res = await getMatchingMatches(params);
      setMatches(res.data.matches || []);
    } catch (err) {
      console.error('Failed to reload matches', err);
    }
  };

  useEffect(() => {
    if (!loading) reloadMatches();
  }, [siteFilter, demandSiteFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    reloadMatches();
  };

  const sorted = useMemo(() => {
    const arr = [...matches];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'quantity': va = a.surplus.quantity; vb = b.surplus.quantity; break;
        case 'value': va = a.surplus.book_value_kEUR || 0; vb = b.surplus.book_value_kEUR || 0; break;
        case 'manufacturer': va = a.surplus.manufacturer_name; vb = b.surplus.manufacturer_name; break;
        case 'offer_site': va = a.surplus.site_name; vb = b.surplus.site_name; break;
        case 'demand_site': va = a.demand.site_name; vb = b.demand.site_name; break;
        default: va = a.surplus.quantity; vb = b.surplus.quantity;
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [matches, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };

  return (
    <div className="space-y-8" data-testid="matching-content">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground text-sm">
            {t('matching.subtitle')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
          {t('matching.refresh')}
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="matching-kpis">
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('matching.matches')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="text-2xl font-bold" data-testid="kpi-total-matches">{stats.total_matches}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('matching.surplusAvailable', { count: stats.total_surplus_skus })}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('matching.matchRate')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="text-2xl font-bold" data-testid="kpi-match-rate">{fmtNum(stats.match_rate_pct)}%</div>
              <p className="text-xs text-muted-foreground mt-1">{t('matching.demandPositions', { count: stats.total_standard_materials })}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('matching.potSavings')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="text-2xl font-bold" data-testid="kpi-savings">{fmtEUR(stats.potential_savings_kEUR)}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('matching.matchedQuantity', { count: fmtNum(stats.matched_quantity, 0) })}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-violet-500">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('matching.sites')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="text-2xl font-bold" data-testid="kpi-sites">{stats.sites_with_surplus + stats.sites_with_demand}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('matching.providers', { count: stats.sites_with_surplus })}, {t('matching.consumers', { count: stats.sites_with_demand })}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && stats?.top_matched_materials?.length > 0 && (
        <Card data-testid="top-materials-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-orange-500" />
              {t('matching.topMaterials')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.top_matched_materials.map((m, i) => (
                <Badge key={i} variant="secondary" className="text-xs py-1 px-2.5" data-testid={`top-material-${i}`}>
                  {m.name} <span className="ml-1.5 font-bold text-orange-600">{m.count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="filters-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('matching.offerSite')}</label>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger data-testid="site-filter">
                  <SelectValue placeholder={t('matching.allSites')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('matching.allSites')}</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.site_id} value={s.site_id}>{s.site_name} ({s.city})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('matching.demandSite')}</label>
              <Select value={demandSiteFilter} onValueChange={setDemandSiteFilter}>
                <SelectTrigger data-testid="demand-site-filter">
                  <SelectValue placeholder={t('matching.allSites')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('matching.allSites')}</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.site_id} value={s.site_id}>{s.site_name} ({s.city})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('matching.manufacturer')}</label>
              <form onSubmit={handleSearchSubmit} className="flex gap-1.5">
                <Input
                  value={manufacturerSearch}
                  onChange={(e) => setManufacturerSearch(e.target.value)}
                  placeholder={t('matching.searchManufacturer')}
                  className="h-9"
                  data-testid="manufacturer-search"
                />
                <Button type="submit" size="sm" variant="secondary" className="h-9 px-3" data-testid="manufacturer-search-btn">
                  <Search className="h-3.5 w-3.5" />
                </Button>
              </form>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : sorted.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Target className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold" data-testid="no-matches">{t('matching.noMatches')}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('matching.noMatchesDesc')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="matches-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">
              {t('matching.matchesFound', { count: sorted.length })}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('manufacturer')}>
                      <span className="flex items-center gap-1">{t('matching.material')} <SortIcon field="manufacturer" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('offer_site')}>
                      <span className="flex items-center gap-1">{t('matching.provider')} <SortIcon field="offer_site" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('demand_site')}>
                      <span className="flex items-center gap-1">{t('matching.consumer')} <SortIcon field="demand_site" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('quantity')}>
                      <span className="flex items-center gap-1 justify-end">{t('matching.quantity')} <SortIcon field="quantity" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('value')}>
                      <span className="flex items-center gap-1 justify-end">{t('matching.bookValue')} <SortIcon field="value" /></span>
                    </TableHead>
                    <TableHead className="text-center">{t('matching.age')}</TableHead>
                    <TableHead className="text-center">{t('matching.width')}</TableHead>
                    <TableHead className="text-right">{t('matching.action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((match) => (
                    <TableRow
                      key={match.match_id}
                      className="group hover:bg-muted/30 cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === match.match_id ? null : match.match_id)}
                      data-testid={`match-row-${match.match_id}`}
                    >
                      <TableCell>
                        <div className="min-w-[180px]">
                          <p className="font-medium text-sm truncate max-w-[240px]">{match.surplus.title || match.surplus.manufacturer_name}</p>
                          <p className="text-xs text-muted-foreground">{match.surplus.manufacturer_name} / {match.surplus.manufacturer_part_number}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Factory className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{match.surplus.site_name}</p>
                            <p className="text-xs text-muted-foreground">{match.surplus.city}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Target className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{match.demand.site_name}</p>
                            <p className="text-xs text-muted-foreground">{match.demand.city}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmtNum(match.surplus.quantity, 0)} {match.surplus.uom}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmtEUR(match.surplus.book_value_kEUR)}
                      </TableCell>
                      <TableCell className="text-center">
                        {match.surplus.age_bucket && (
                          <Badge variant="outline" className={`text-[10px] ${AGE_COLORS[match.surplus.age_bucket] || ''}`}>
                            {AGE_LABELS[match.surplus.age_bucket] || match.surplus.age_bucket}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {match.width_match === true && (
                          <Badge className="bg-emerald-500 text-white text-[10px]">{t('matching.widthFit')}</Badge>
                        )}
                        {match.width_match === false && (
                          <Badge variant="destructive" className="text-[10px]">{t('matching.widthDeviation')}</Badge>
                        )}
                        {match.width_match === null && (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                          onClick={(e) => { e.stopPropagation(); navigate(`/material/${match.surplus.material_id}`); }}
                          data-testid={`view-material-${match.surplus.sku_id}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          {t('matching.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const COCKPIT_COLORS = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#EAB308', '#06B6D4', '#EF4444', '#14B8A6', '#6366F1'];
const AGE_COLORS_MAP = { '0_90': '#10B981', '91_180': '#84CC16', '181_360': '#F59E0B', 'gt_360': '#EF4444', 'unknown': '#94A3B8' };

const CockpitContent = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCockpit();
  }, []);

  const loadCockpit = async () => {
    setLoading(true);
    try {
      const res = await getMatchingCockpit();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load cockpit', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="cockpit-loading">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { kpis, charts } = data;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 text-xs">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: <span className="font-mono font-medium">{typeof p.value === 'number' ? p.value.toLocaleString('de-DE') : p.value}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="cockpit-content">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="cockpit-kpis">
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Surplus-SKUs</p>
            <p className="text-2xl font-bold mt-1" data-testid="kpi-surplus">{kpis.total_surplus_skus}</p>
            <p className="text-[10px] text-muted-foreground">{kpis.total_sites} Standorte</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Standardmaterial</p>
            <p className="text-2xl font-bold mt-1" data-testid="kpi-standard">{kpis.total_standard_materials}</p>
            <p className="text-[10px] text-muted-foreground">Bedarfspositionen</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Matches</p>
            <p className="text-2xl font-bold mt-1" data-testid="kpi-matches">{kpis.total_matches}</p>
            <p className="text-[10px] text-muted-foreground">{kpis.match_rate_pct}% Match-Rate</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Einsparungen</p>
            <p className="text-2xl font-bold mt-1" data-testid="kpi-savings-total">{kpis.total_savings_kEUR} kEUR</p>
            <p className="text-[10px] text-muted-foreground">Potenzial</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Umverteilungen</p>
            <p className="text-2xl font-bold mt-1" data-testid="kpi-redist">{kpis.completed_redistributions}</p>
            <p className="text-[10px] text-muted-foreground">{kpis.total_orders} Bestellungen</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <Card data-testid="category-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Layers className="h-4 w-4 text-orange-500" />
              Surplus nach Kategorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {charts.category_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={charts.category_distribution} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Anzahl" fill="#F97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-10">Keine Daten</p>
            )}
          </CardContent>
        </Card>

        {/* Age Distribution */}
        <Card data-testid="age-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-500" />
              Altersverteilung
            </CardTitle>
          </CardHeader>
          <CardContent>
            {charts.age_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={charts.age_distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {charts.age_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={AGE_COLORS_MAP[entry.key] || COCKPIT_COLORS[index % COCKPIT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-10">Keine Daten</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Site Comparison */}
        <Card data-testid="site-comparison-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Factory className="h-4 w-4 text-emerald-500" />
              Standort-Vergleich: Surplus vs. Bedarf
            </CardTitle>
          </CardHeader>
          <CardContent>
            {charts.site_comparison?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.site_comparison} margin={{ left: 10, right: 10, top: 5, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="site_name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="surplus" name="Surplus" fill="#F97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="demand" name="Bedarf" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="matched" name="Matches" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-10">Keine Daten</p>
            )}
          </CardContent>
        </Card>

        {/* Savings by Site */}
        <Card data-testid="savings-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-violet-500" />
              Einsparpotenzial nach Standort (kEUR)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {charts.savings_by_site?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.savings_by_site} margin={{ left: 10, right: 20, top: 5, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="site_name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="savings_kEUR" name="Einsparung (kEUR)" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-10">Keine Einsparungsdaten</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main combined page
export const MatchingDashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get('tab') || 'matching';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === 'matching') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  useEffect(() => {
    const tab = searchParams.get('tab') || 'matching';
    setActiveTab(tab);
  }, [searchParams]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="matching-dashboard">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Page Title */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="matching-title">
            <ArrowRightLeft className="h-7 w-7 text-orange-500" />
            {t('matching.title')}
          </h1>
        </div>

        {/* Top-level Tab Bar */}
        <div className="flex gap-1 border-b" data-testid="matching-tab-bar">
          <button
            onClick={() => handleTabChange('matching')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'matching'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-matching"
          >
            <ArrowRightLeft className="h-4 w-4" />
            Standort-Matching
          </button>
          <button
            onClick={() => handleTabChange('standard')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'standard'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-standard"
          >
            <ClipboardList className="h-4 w-4" />
            Standard-Materialien
          </button>
          <button
            onClick={() => handleTabChange('cockpit')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'cockpit'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-cockpit"
          >
            <Gauge className="h-4 w-4" />
            Cockpit
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'matching' ? (
          <MatchingContent />
        ) : activeTab === 'cockpit' ? (
          <CockpitContent />
        ) : (
          <StandardMaterialsContent />
        )}
      </main>
    </div>
  );
};

export default MatchingDashboardPage;
